/**
 * BAN address autocomplete with department resolution from postal code.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useDepartments } from '@/hooks/useDepartments';
import { resolveDepartmentSelection } from '@/utils/department-lookup-utils';
import { getErrorMessage } from '@/utils/errors-utils';
import { searchGeography } from '@/services/geography.service';
import type { AddressSelectionResult, GeographySuggestion } from '@/types/geography.types';
import { getCompanyCountry } from '@/stores/themeStore';

/** Options for {@link useAddressAutocomplete}. */
interface UseAddressAutocompleteOptions {
  minCharacters?: number;
  debounceMs?: number;
  onSelection: (result: AddressSelectionResult) => void;
}

/** BAN address autocomplete with department resolution from postal code. */
export function useAddressAutocomplete({
  minCharacters = 3,
  debounceMs = 300,
  onSelection,
}: UseAddressAutocompleteOptions) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<GeographySuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const { data: departments = [] } = useDepartments('FR');
  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchSuggestions = useCallback(async (searchQuery: string) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    setIsLoading(true);
    setError(null);

    try {
      const mappedSuggestions = await searchGeography(searchQuery, {
        limit: 5,
        signal: abortControllerRef.current.signal,
      });

      setSuggestions(mappedSuggestions);
      setIsOpen(mappedSuggestions.length > 0);
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        setError(getErrorMessage(err));
        setSuggestions([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (!query || query.length < minCharacters) {
      setSuggestions([]);
      setIsOpen(false);
      setIsLoading(false);
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      void fetchSuggestions(query);
    }, debounceMs);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [query, minCharacters, debounceMs, fetchSuggestions]);

  const handleSelectSuggestion = useCallback(
    (suggestion: GeographySuggestion) => {
      const postalCode = suggestion.postalCode || '';
      const city = suggestion.city || '';
      const addressLine1 = suggestion.value;

      const resolved = resolveDepartmentSelection(
        departments,
        postalCode,
        suggestion.departmentCode,
      );

      onSelection({
        addressLine1,
        postalCode,
        city,
        department: resolved.label,
        departmentCode: resolved.code,
        country: getCompanyCountry(),
      });

      setIsOpen(false);
    },
    [onSelection, departments],
  );

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
    setIsOpen(false);
  }, []);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return {
    query,
    setQuery,
    suggestions,
    isLoading,
    error,
    isOpen,
    handleSelectSuggestion,
    clearSuggestions,
  };
}
